import { AnimatePresence, motion } from "motion/react";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/cn";

/** Short, non-blocking notices (e.g. "Cannot add X without a prescription reference").
 * Announced politely (assertive for errors); auto-dismiss after 4 s; never steal focus. */
type Tone = "error" | "success" | "info";
type Toast = { id: number; tone: Tone; text: string };
type Api = { show: (text: string, tone?: Tone) => void };
const Ctx = createContext<Api>({ show: () => undefined });
export const useToast = () => useContext(Ctx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (text: string, tone: Tone = "info") => {
      const id = ++seq.current;
      setToasts((t) => [...t.filter((x) => x.text !== text).slice(-2), { id, tone, text }]);
      setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );
  const api = useMemo(() => ({ show }), [show]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-24 z-[70] flex flex-col items-center gap-2 px-4">
        <div role="status" aria-live="polite" className="sr-only">
          {toasts.filter((t) => t.tone !== "error").map((t) => t.text).join(". ")}
        </div>
        <div aria-live="assertive" className="sr-only">
          {toasts.filter((t) => t.tone === "error").map((t) => t.text).join(". ")}
        </div>
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              className={cn(
                "pointer-events-auto flex max-w-[520px] items-center gap-2.5 rounded-[12px] px-4 py-3 text-base font-semibold shadow-[0_14px_36px_-12px_rgb(15_23_42/0.45)]",
                t.tone === "error" ? "bg-[var(--danger-fill)] text-white" : t.tone === "success" ? "bg-[var(--success-fill)] text-white" : "bg-inverse text-on-inverse",
              )}
              aria-hidden="true"
            >
              {t.tone === "error" ? <AlertCircle className="h-4 w-4 shrink-0" /> : t.tone === "success" ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <Info className="h-4 w-4 shrink-0" />}
              <span>{t.text}</span>
              <button type="button" onClick={() => dismiss(t.id)} className="-mr-1 ml-1 rounded p-0.5 opacity-70 hover:opacity-100" aria-label="Dismiss" tabIndex={-1}>
                <X className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}
